import { AxiosResponse } from 'axios'

import { signupApi } from './signup'

import {
  AddOnEndpointResponse,
  AutoLoginInfo,
  Channel,
  CompleteDirectSchema,
  DVLAVehicleResponse,
  ListItemBEResponse,
  OptionItem,
  PaymentPlan,
  PaymentSchedule,
  PriceAPIResponse,
  PriceV2ApiResponse,
  QuoteV2,
  RemoveAddOnMutationType,
  RequiredChecksAPIResponse,
  SelectAddOnMutationType,
  SelectProductPlanMutationType,
  SetNCDMutationType,
  SetStudentMutationType,
  SetVehicleRegPlateMutationType,
  UpdateInceptionDateMutationType,
  UpdateQuoteMutationType,
  ValidateDrivingLicenceMutation,
  ValidateDrivingLicenceMutationResponse,
  VehicleEndpointResponse,
} from 'types'

const quotesApi = signupApi.injectEndpoints({
  endpoints: (builder) => ({
    applyDiscount: builder.mutation<AxiosResponse, { discountCode: string; quoteId: string }>({
      query: ({ discountCode, quoteId }) => ({
        url: `quotes/${quoteId}/discounts/${discountCode}`,
        method: 'PUT',
      }),
      invalidatesTags: ['Price'],
    }),
    completeDirect: builder.mutation<AxiosResponse, CompleteDirectSchema>({
      query: (schema) => ({
        url: 'quotes/complete',
        method: 'POST',
        data: schema,
      }),
    }),
    validateDrivingLicenceNumber: builder.mutation<
      ValidateDrivingLicenceMutationResponse,
      ValidateDrivingLicenceMutation
    >({
      query: (params) => ({
        url: 'quotes/validations/licence-number',
        method: 'POST',
        data: params,
      }),
    }),
    getAddons: builder.query<AddOnEndpointResponse, string>({
      query: (quoteId) => ({ url: `quotes/${quoteId}/add-ons`, method: 'GET' }),
      providesTags: (result, error, id) => [{ type: 'Addons', id }],
    }),
    getClaimTypes: builder.query<ListItemBEResponse[], void>({
      query: () => ({ url: 'claim-types', method: 'GET' }),
    }),
    getConvictionCodes: builder.query<ListItemBEResponse[], void>({
      query: () => ({ url: 'conviction-codes', method: 'GET' }),
    }),
    getModificationCodes: builder.query<OptionItem[], void>({
      query: () => ({ url: 'modification-codes', method: 'GET' }),
    }),
    getAutoLoginInfo: builder.query<AutoLoginInfo, string>({
      query: (quoteId) => ({ url: `quotes/${quoteId}/account-token`, method: 'POST' }),
    }),
    getCountries: builder.query<ListItemBEResponse[], void>({
      query: () => ({ url: `countries`, method: 'GET' }),
    }),
    getCourses: builder.query<ListItemBEResponse[], void>({
      query: () => ({ url: `student/courses`, method: 'GET' }),
    }),
    getQuoteV2: builder.query<QuoteV2, { quoteId: string }>({
      query: ({ quoteId }) => ({
        url: `v2/quotes/${quoteId}`,
        method: 'GET',
      }),
      transformResponse: (quote: QuoteV2) => {
        if (!quote.channel.fromUswitch) {
          return quote
        }

        // Transforms response and adds uswitch as channel name to replace CONFUSED
        return {
          ...quote,
          channel: {
            ...quote.channel,
            name: Channel.USWITCH,
          },
        }
      },
      providesTags: (result, error, args) => [{ type: 'Quote', id: args.quoteId }],
    }),
    updateQuote: builder.mutation<AxiosResponse, UpdateQuoteMutationType>({
      query: ({ quoteId, bodyParams }) => ({
        url: `quotes/${quoteId}`,
        method: 'PATCH',
        data: bodyParams,
      }),
      invalidatesTags: ['Quote', 'Price', 'Addons'],
    }),
    getPriceV2: builder.query<PriceV2ApiResponse, string>({
      query: (quoteId) => ({ url: `v2/quotes/${quoteId}/price`, method: 'GET' }),
      providesTags: (result, error, id) => [{ type: 'Price', id }],
    }),
    getPrice: builder.query<PriceAPIResponse, string>({
      query: (quoteId) => ({ url: `quotes/${quoteId}/price`, method: 'GET' }),
      providesTags: (result, error, id) => [{ type: 'Price', id }],
    }),
    getRequiredChecks: builder.query<
      RequiredChecksAPIResponse,
      { quoteId: string; paymentType?: PaymentPlan }
    >({
      query: ({ quoteId, paymentType }) => ({
        url: `quotes/${quoteId}/checks`,
        method: 'GET',
        params: paymentType ? { payment: paymentType.toLocaleUpperCase() } : undefined,
      }),
      providesTags: (result, error, { quoteId, paymentType }) => {
        const idArray = [quoteId]
        if (paymentType) {
          idArray.push(paymentType)
        }
        return [{ type: 'RequiredChecks', id: idArray.join('/') }]
      },
    }),
    getUniversities: builder.query<ListItemBEResponse[], void>({
      query: () => ({ url: `student/universities`, method: 'GET' }),
    }),
    selectProductPlan: builder.mutation<AxiosResponse, SelectProductPlanMutationType>({
      query: ({ id, plan }) => ({
        url: `quotes/${id}/plan`,
        method: 'PUT',
        data: { plan },
      }),
      invalidatesTags: ['Addons', 'Price', 'Quote'],
    }),
    selectAddOn: builder.mutation<AxiosResponse, SelectAddOnMutationType>({
      query: ({ quoteId, addOnId, optionId }) => ({
        url: `quotes/${quoteId}/add-ons/${addOnId}/options/${optionId || addOnId}`,
        method: 'PUT',
      }),
      invalidatesTags: ['Addons', 'Price', 'Quote'],
    }),
    setNCDYears: builder.mutation<AxiosResponse, SetNCDMutationType>({
      query: ({ quoteId, ncdValues }) => ({
        url: `quotes/${quoteId}/drivers/policy-holder/no-claims/allocation`,
        method: 'PUT',
        data: { ...ncdValues },
      }),
      invalidatesTags: ['Addons', 'Price', 'Quote'],
    }),
    setStudentQuestions: builder.mutation<AxiosResponse, SetStudentMutationType>({
      query: ({ quoteId, studentQuestions, secondaryEmployment }) => ({
        url: `quotes/${quoteId}/drivers/policy-holder/student`,
        method: 'PUT',
        data: { ...studentQuestions, secondaryEmployment: { ...secondaryEmployment } },
      }),
      invalidatesTags: ['Addons', 'Price', 'Quote'],
    }),
    removeAddOn: builder.mutation<AxiosResponse, RemoveAddOnMutationType>({
      query: ({ quoteId, addOnId }) => ({
        url: `quotes/${quoteId}/add-ons/${addOnId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Addons', 'Price', 'Quote'],
    }),
    updateInceptionDate: builder.mutation<AxiosResponse, UpdateInceptionDateMutationType>({
      query: ({ quoteId, inceptionDate }) => ({
        url: `quotes/${quoteId}/inception-date`,
        method: 'PUT',
        data: { inceptionDate },
      }),
      invalidatesTags: ['Price', 'Quote'],
    }),
    getOccupations: builder.query<ListItemBEResponse[], void>({
      query: () => ({ url: `occupations`, method: 'GET' }),
    }),
    getIndustries: builder.query<ListItemBEResponse[], void>({
      query: () => ({ url: `industries`, method: 'GET' }),
    }),
    getPaymentSchedule: builder.query<PaymentSchedule, string>({
      query: (quoteId) => ({ url: `quotes/${quoteId}/payment-schedule`, method: 'GET' }),
      providesTags: (result, error, id) => [{ type: 'PaymentSchedule', id }],
    }),
    updateVehicleRegPlate: builder.mutation<AxiosResponse, SetVehicleRegPlateMutationType>({
      query: ({ quoteId, regPlate, foreignRegPlate }) => ({
        url: `quotes/${quoteId}/vehicle/registration`,
        method: 'PUT',
        data: { regPlate, foreignRegPlate },
      }),
      invalidatesTags: ['Quote', 'Vehicle'],
    }),
    updateLicenceCountry: builder.mutation<AxiosResponse, { quoteId: string; countryCode: string }>(
      {
        query: ({ quoteId, countryCode }) => ({
          url: `quotes/${quoteId}/drivers/policy-holder/licence/country`,
          method: 'PUT',
          data: { countryCode },
        }),
        invalidatesTags: ['Quote'],
      },
    ),
    updateEmail: builder.mutation<AxiosResponse, { quoteId: string; email: string }>({
      query: ({ quoteId, email }) => ({
        url: `quotes/${quoteId}/email`,
        method: 'PUT',
        data: { email },
      }),
      invalidatesTags: ['Quote'],
    }),
    updatePhoneNumber: builder.mutation<AxiosResponse, { quoteId: string; phone: string }>({
      query: ({ quoteId, phone }) => ({
        url: `quotes/${quoteId}/drivers/policy-holder/phone`,
        method: 'PUT',
        data: { phone },
      }),
      invalidatesTags: ['Quote'],
    }),
    updateDrivingLicenceNumber: builder.mutation<
      AxiosResponse,
      { quoteId: string; driverId: string; licenceNumber: string }
    >({
      query: ({ quoteId, driverId, licenceNumber }) => ({
        url: `quotes/${quoteId}/drivers/licence/number`,
        method: 'PUT',
        data: { licenceNumber, driverId },
      }),
      invalidatesTags: ['Quote'],
    }),
    getDvlaData: builder.query<DVLAVehicleResponse, string>({
      query: (regPlate) => ({
        url: 'dvla',
        params: { registration: regPlate },
        method: 'GET',
      }),
    }),
    getVehicleManufacturers: builder.query<string[], void>({
      query: () => ({ url: 'vehicles/manufacturers', method: 'GET' }),
    }),
    getVehicleModels: builder.query<string[], string>({
      query: (manufacturer: string) => ({
        url: `vehicles/manufacturers`,
        method: 'GET',
        params: { manufacturer },
      }),
    }),
    getAllVehicleDetails: builder.query<
      VehicleEndpointResponse[],
      { manufacturer: string; model: string }
    >({
      query: ({ manufacturer, model }) => ({
        url: `vehicles/trims`,
        method: 'GET',
        params: { manufacturer, model },
      }),
    }),
    sendDeviceInfo: builder.query<AxiosResponse, { quoteId: string }>({
      query: ({ quoteId }) => ({
        url: `quotes/${quoteId}/device-info`,
        method: 'POST',
      }),
    }),
  }),
})

const {
  useApplyDiscountMutation,
  useCompleteDirectMutation,
  useValidateDrivingLicenceNumberMutation,
  useGetAddonsQuery,
  useGetAutoLoginInfoQuery,
  useGetClaimTypesQuery,
  useGetCountriesQuery,
  useGetCoursesQuery,
  useGetConvictionCodesQuery,
  useGetModificationCodesQuery,
  useGetQuoteV2Query,
  useLazyGetQuoteV2Query,
  useUpdateQuoteMutation,
  useGetPriceQuery,
  useGetPriceV2Query,
  useGetRequiredChecksQuery,
  useGetUniversitiesQuery,
  useSelectProductPlanMutation,
  useSelectAddOnMutation,
  useSetNCDYearsMutation,
  useSetStudentQuestionsMutation,
  useRemoveAddOnMutation,
  useUpdateInceptionDateMutation,
  useGetOccupationsQuery,
  useGetIndustriesQuery,
  useGetPaymentScheduleQuery,
  useUpdateVehicleRegPlateMutation,
  useUpdateLicenceCountryMutation,
  useUpdateEmailMutation,
  useUpdatePhoneNumberMutation,
  useUpdateDrivingLicenceNumberMutation,
  useGetDvlaDataQuery,
  useLazyGetDvlaDataQuery,
  useGetVehicleManufacturersQuery,
  useGetVehicleModelsQuery,
  useLazyGetVehicleModelsQuery,
  useLazyGetAllVehicleDetailsQuery,
  useSendDeviceInfoQuery,
} = quotesApi

export {
  quotesApi,
  useApplyDiscountMutation,
  useCompleteDirectMutation,
  useGetAddonsQuery,
  useGetAutoLoginInfoQuery,
  useGetClaimTypesQuery,
  useGetConvictionCodesQuery,
  useGetCountriesQuery,
  useGetCoursesQuery,
  useGetDvlaDataQuery,
  useGetIndustriesQuery,
  useGetModificationCodesQuery,
  useGetOccupationsQuery,
  useGetPaymentScheduleQuery,
  useGetPriceQuery,
  useGetPriceV2Query,
  useGetQuoteV2Query,
  useGetRequiredChecksQuery,
  useGetUniversitiesQuery,
  useGetVehicleManufacturersQuery,
  useGetVehicleModelsQuery,
  useLazyGetAllVehicleDetailsQuery,
  useLazyGetDvlaDataQuery,
  useLazyGetQuoteV2Query,
  useLazyGetVehicleModelsQuery,
  useRemoveAddOnMutation,
  useSelectAddOnMutation,
  useSelectProductPlanMutation,
  useSendDeviceInfoQuery,
  useSetNCDYearsMutation,
  useSetStudentQuestionsMutation,
  useUpdateDrivingLicenceNumberMutation,
  useUpdateEmailMutation,
  useUpdateInceptionDateMutation,
  useUpdateLicenceCountryMutation,
  useUpdatePhoneNumberMutation,
  useUpdateQuoteMutation,
  useUpdateVehicleRegPlateMutation,
  useValidateDrivingLicenceNumberMutation,
}
